import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatTitle } from "@/lib/typography";
import { Glossaire } from "./Glossaire";
import { z } from "zod";

const inputSchema = z.object({
  prixTTC: z.number().min(0),
  tauxCommission: z.number().min(0).max(100),
  fraisFulfillment: z.number().min(0),
  stockageMensuel: z.number().min(0),
  moisRotation: z.number().min(0),
  cogs: z.number().min(0),
  tauxRetours: z.number().min(0).max(100),
  coupons: z.number().min(0),
  spendSP: z.number().min(0),
  spendSB: z.number().min(0),
  spendSD: z.number().min(0),
  cvr: z.number().min(0).max(100),
  acosCible: z.number().min(0).max(100),
});

export const OptimizerPage = () => {
  const [inputs, setInputs] = useState({
    prixTTC: 25,
    tauxCommission: 15,
    fraisFulfillment: 3.5,
    stockageMensuel: 0.5,
    moisRotation: 2,
    cogs: 8,
    tauxRetours: 2,
    coupons: 0,
    spendSP: 100,
    spendSB: 30,
    spendSD: 20,
    cvr: 8,
    acosCible: 35,
  });

  const [clicks, setClicks] = useState(20);
  const [impressions, setImpressions] = useState(1000);
  const [conversions, setConversions] = useState(2);
  const [stockDays, setStockDays] = useState(25);

  const calc = useMemo(() => {
    try {
      const validated = inputSchema.parse(inputs);
      const prixHT = validated.prixTTC / 1.2;
      const commission = prixHT * (validated.tauxCommission / 100);
      const fba = validated.fraisFulfillment + (validated.stockageMensuel * validated.moisRotation) / 12;
      const revenuNet = validated.prixTTC * (1 - validated.tauxRetours / 100);
      const fraisPlateforme = commission + fba + validated.coupons;
      const margeContribution = revenuNet - validated.cogs - fraisPlateforme;
      const totalSpend = validated.spendSP + validated.spendSB + validated.spendSD;
      const ventesAttribuees = totalSpend > 0 ? totalSpend / (validated.acosCible / 100) : 0;
      const acos = ventesAttribuees > 0 ? (totalSpend / ventesAttribuees) * 100 : 0;
      const tacos = ventesAttribuees + revenuNet > 0 ? (totalSpend / (ventesAttribuees + revenuNet)) * 100 : 0;
      const cpcSeuil = prixHT * (validated.cvr / 100) * (validated.acosCible / 100);
      const bidMaxAuto = cpcSeuil * 0.85;
      const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;

      return {
        prixHT: prixHT.toFixed(2),
        commission: commission.toFixed(2),
        fba: fba.toFixed(2),
        revenuNet: revenuNet.toFixed(2),
        fraisPlateforme: fraisPlateforme.toFixed(2),
        margeContribution: margeContribution.toFixed(2),
        acos: acos.toFixed(2),
        tacos: tacos.toFixed(2),
        cpcSeuil: cpcSeuil.toFixed(2),
        bidMaxAuto: bidMaxAuto.toFixed(2),
        ctr: ctr.toFixed(2),
      };
    } catch {
      return null;
    }
  }, [inputs, clicks, impressions, conversions]);

  const watchers = useMemo(() => {
    const alerts: Array<{ type: "warning" | "success" | "info"; message: string }> = [];
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    if (ctr < 0.25 && impressions > 1000) {
      alerts.push({ type: "warning", message: "CTR <0,25 % et Impressions >1 000 — retravailler créa et titre" });
    }
    if (clicks >= 20 && conversions === 0) {
      alerts.push({ type: "warning", message: "Clics ≥20 et Conversions = 0 — baisser −15 % et ajouter négatifs" });
    }
    if (calc && parseFloat(calc.acos) <= inputs.acosCible - 10) {
      alerts.push({ type: "success", message: "ACoS ≤ cible −10 % — +10 % ou élargir Exact" });
    }
    if (stockDays < 15) {
      alerts.push({ type: "warning", message: "Couverture stock <15 jours — geler le scale" });
    }
    return alerts;
  }, [clicks, impressions, conversions, stockDays, inputs.acosCible, calc]);

  const handle = (k: keyof typeof inputs) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setInputs((s) => ({ ...s, [k]: parseFloat(e.target.value || "0") }));

  const glossaire = [
    { term: "Commission", definition: "Frais prélevés par Amazon sur chaque vente" },
    { term: "FBA", definition: "Fulfillment By Amazon — frais de stockage et expédition" },
    { term: "COGS", definition: "Cost Of Goods Sold — coût de revient produit" },
    { term: "Marge contribution", definition: "Revenu net − COGS − Frais plateforme" },
    { term: "CPC seuil", definition: "CPC maximum pour atteindre l'ACoS cible" },
    { term: "Bid max", definition: "Enchère maximale recommandée (CPC seuil × 85 %)" },
    { term: "TACoS", definition: "Total Advertising Cost of Sale — dépense pub / CA total" },
  ];

  return (
    <div className="space-y-4">
      {/* 1) PARAMÈTRES ÉCONOMIQUES */}
      <Card>
        <CardHeader>
          <CardTitle>{formatTitle("1) Paramètres économiques — entrées utilisateur")}</CardTitle>
          <CardDescription>Saisir les données produit et dépenses publicitaires</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="text-sm">
              Prix TTC (€)
              <input type="number" step="0.01" value={inputs.prixTTC} onChange={handle("prixTTC")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Taux Commission (%)
              <input type="number" step="0.01" value={inputs.tauxCommission} onChange={handle("tauxCommission")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Frais Fulfillment (€)
              <input type="number" step="0.01" value={inputs.fraisFulfillment} onChange={handle("fraisFulfillment")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Stockage mensuel (€)
              <input type="number" step="0.01" value={inputs.stockageMensuel} onChange={handle("stockageMensuel")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Mois rotation
              <input type="number" step="0.1" value={inputs.moisRotation} onChange={handle("moisRotation")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              COGS (€)
              <input type="number" step="0.01" value={inputs.cogs} onChange={handle("cogs")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Taux Retours (%)
              <input type="number" step="0.01" value={inputs.tauxRetours} onChange={handle("tauxRetours")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Coupons (€)
              <input type="number" step="0.01" value={inputs.coupons} onChange={handle("coupons")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Spend SP (€)
              <input type="number" step="0.01" value={inputs.spendSP} onChange={handle("spendSP")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Spend SB (€)
              <input type="number" step="0.01" value={inputs.spendSB} onChange={handle("spendSB")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Spend SD (€)
              <input type="number" step="0.01" value={inputs.spendSD} onChange={handle("spendSD")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              CVR (%)
              <input type="number" step="0.01" value={inputs.cvr} onChange={handle("cvr")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              ACoS Cible (%)
              <input type="number" step="0.01" value={inputs.acosCible} onChange={handle("acosCible")} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* 2) FORMULES — CALCUL EN TEMPS RÉEL */}
      {calc && (
        <Card>
          <CardHeader>
            <CardTitle>{formatTitle("2) Formules — calcul en temps réel")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">Prix HT</div>
                <div className="text-lg font-semibold">{calc.prixHT} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">Commission</div>
                <div className="text-lg font-semibold">{calc.commission} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">FBA</div>
                <div className="text-lg font-semibold">{calc.fba} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">Revenu net retours</div>
                <div className="text-lg font-semibold">{calc.revenuNet} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">Frais plateforme</div>
                <div className="text-lg font-semibold">{calc.fraisPlateforme} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">Marge contribution</div>
                <div className="text-lg font-semibold text-primary">{calc.margeContribution} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">ACoS</div>
                <div className="text-lg font-semibold">{calc.acos} %</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">TACoS</div>
                <div className="text-lg font-semibold">{calc.tacos} %</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">CPC seuil</div>
                <div className="text-lg font-semibold">{calc.cpcSeuil} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">Bid max auto</div>
                <div className="text-lg font-semibold text-primary">{calc.bidMaxAuto} €</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">CTR</div>
                <div className="text-lg font-semibold">{calc.ctr} %</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3) WATCHERS — STABILITÉ ET RÉACTIVITÉ */}
      <Card>
        <CardHeader>
          <CardTitle>{formatTitle("3) Watchers — stabilité et réactivité")}</CardTitle>
          <CardDescription>Données campagne pour détecter les actions nécessaires</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="text-sm">
              Clics
              <input type="number" value={clicks} onChange={(e) => setClicks(parseInt(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Impressions
              <input type="number" value={impressions} onChange={(e) => setImpressions(parseInt(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Conversions
              <input type="number" value={conversions} onChange={(e) => setConversions(parseInt(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Stock (jours)
              <input type="number" value={stockDays} onChange={(e) => setStockDays(parseInt(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
          </div>

          <div className="pt-3 border-t">
            <div className="text-sm font-semibold mb-2">{formatTitle("Alertes")}</div>
            {watchers.length === 0 ? (
              <div className="text-sm text-muted-foreground">Aucune alerte — campagnes stables</div>
            ) : (
              <div className="space-y-2">
                {watchers.map((w, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Badge variant={w.type === "warning" ? "destructive" : w.type === "success" ? "default" : "outline"} className="shrink-0 mt-0.5">
                      {w.type === "warning" ? "⚠" : w.type === "success" ? "✓" : "ℹ"}
                    </Badge>
                    <p className="text-sm text-muted-foreground">{w.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 4) GLOSSAIRE */}
      <Glossaire entries={glossaire} />
    </div>
  );
};
